/**
 * RiderFeeToSupplierPage Component
 * 라이더 → 협력사 수수료 관리 페이지
 *
 * 협력사가 라이더에게 설정하는 수수료 (라이더별 수수료 관리)
 *
 * - 본사 사용자: 협력사 선택 후 수수료 조회 (읽기 전용)
 * - 협력사 사용자: 바로 자신의 라이더 수수료 관리
 */
import React, { useState, useEffect, useCallback } from "react";
import { getItem } from '@/shared/utils/storage';
import { ArrowLeftIcon } from '@/shared/components/icons/Icons';
import SupplierFeeManagementPage from './SupplierFeeManagementPage';
import SupplierSelectionPage from '@/features/partner/supplierStatus/components/SupplierSelectionPage';
import Loading from '@/shared/components/feedback/Loading';

const RiderFeeToSupplierPage: React.FC = () => {
    // ========================================
    // 1. State - 로컬 상태 관리
    // ========================================
    const [userType, setUserType] = useState<string>('');
    const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
    const [selectedSupplierName, setSelectedSupplierName] = useState<string>('');
    const [isInitialized, setIsInitialized] = useState<boolean>(false);

    // ========================================
    // 2. Side Effects - 초기 데이터 로드
    // ========================================
    useEffect(() => {
        const userTypeValue = getItem<string>('USER_TYPE');
        const userId = getItem<string>('USER_ID');
        setUserType(userTypeValue || '');

        // 협력사 사용자는 자동으로 자신의 ID 설정
        if (userTypeValue === 'SUPPLIER' && userId) {
            setSelectedSupplierId(userId);
        }

        setIsInitialized(true);
    }, []);

    // ========================================
    // 3. Event Handlers
    // ========================================
    /**
     * 협력사 선택 (본사 사용자용)
     */
    const handleSupplierSelect = useCallback((supplierId: string, supplierName: string) => {
        setSelectedSupplierId(supplierId);
        setSelectedSupplierName(supplierName);
    }, []);

    /**
     * 협력사 목록으로 돌아가기 (본사 사용자용)
     */
    const handleBackToSelection = useCallback(() => {
        setSelectedSupplierId('');
        setSelectedSupplierName('');
    }, []);

    // ========================================
    // 4. Render - 초기화 대기
    // ========================================
    if (!isInitialized) {
        return <Loading message="사용자 정보를 확인하는 중..." />;
    }

    // ========================================
    // 5. Render - 본사 사용자 협력사 선택 페이지
    // ========================================
    if (userType === 'HEADQUARTERS' && !selectedSupplierId) {
        return (
            <SupplierSelectionPage
                onSupplierSelect={handleSupplierSelect}
                pageTitle="라이더 → 협력사 수수료"
            />
        );
    }

    // ========================================
    // 6. Render - 수수료 관리 페이지
    // ========================================
    return (
        <div className="supplier-selection-page">
            {/* 페이지 제목 */}
            <div className="page-title-wrapper">
                <h1 className="page-title">
                    라이더 → 협력사 수수료
                    {userType === 'HEADQUARTERS' && selectedSupplierName && (
                        <span style={{
                            fontSize: '1rem',
                            fontWeight: 500,
                            color: 'var(--vroong-green)',
                            marginLeft: '12px',
                        }}>
                            {selectedSupplierName}
                        </span>
                    )}
                </h1>
                {/* 본사 사용자용 뒤로가기 버튼 */}
                {userType === 'HEADQUARTERS' && (
                    <button
                        className="search-btn"
                        onClick={handleBackToSelection}
                        style={{ marginLeft: 'auto' }}
                    >
                        <ArrowLeftIcon width={16} height={16} />
                        <span>협력사 목록으로</span>
                    </button>
                )}
            </div>

            {/* 수수료 관리 컴포넌트 */}
            <SupplierFeeManagementPage selectedSupplierId={selectedSupplierId} />
        </div>
    );
};

export default RiderFeeToSupplierPage;
